const API_BASE_URL = import.meta.env.VITE_STRIPE_API_URL;

export class StripeApiError extends Error {
  status?: number;
  details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = "StripeApiError";
    this.status = status;
    this.details = details;
  }
}

const buildUrl = (path: string) => {
  if (!API_BASE_URL) {
    throw new StripeApiError(
      "Stripe API base URL (VITE_STRIPE_API_URL) is not configured."
    );
  }

  // Remove trailing slash from base and leading slash from path, then concatenate
  const base = API_BASE_URL.replace(/\/$/, '');
  const relativePath = path.replace(/^\//, '');
  return `${base}/${relativePath}`;
};

const parseJsonSafely = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const post = async <TResponse>(
  path: string,
  body: object
): Promise<TResponse> => {
  const response = await fetch(buildUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const responseBody = await parseJsonSafely(response);

  if (!response.ok) {
    const errorMessage =
      (responseBody && (responseBody as { error?: string }).error) ||
      "Stripe API request failed.";
    throw new StripeApiError(errorMessage, response.status, responseBody);
  }

  return responseBody as TResponse;
};

export interface CreateCheckoutSessionRequest {
  businessId: string;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  trialPeriodDays?: number;
  allowPromotionCodes?: boolean;
}

export interface CreateCheckoutSessionResponse {
  checkoutUrl: string;
  sessionId: string;
}

export const createCheckoutSession = async (
  payload: CreateCheckoutSessionRequest
) => {
  return post<CreateCheckoutSessionResponse>(
    "/v1/checkout/create-session",
    payload
  );
};

export interface CreatePortalSessionRequest {
  businessId: string;
  returnUrl: string;
}

export interface CreatePortalSessionResponse {
  portalUrl: string;
}

export const createPortalSession = async (
  payload: CreatePortalSessionRequest
) => {
  return post<CreatePortalSessionResponse>("/v1/portal/create-session", payload);
};

